import { z } from 'zod'

import { clinicIdSchema } from '../../entities/clinic'
import { fileKeySchema } from '../../entities/file'
import { regionIdSchema } from '../../entities/region'
import { staffRoleSchema } from '../../entities/staff'
import {
  genericTaskDataSchema,
  procedureTypeSchema,
  taskCommentSchema,
  taskDataSchema,
  taskListStatusSchema,
  taskStatusSchema,
  taskTypeSchema,
} from '../../entities/task'
import { MAX_FILE_ATTACHMENTS } from './file'

// ─── POST /tasks ────────────────────────────────────────────────────

export const postGenericTaskBodySchema = z
  .object({
    title: z.string().min(1),
    patientId: z.uuid().nullable().optional(),
    /** Must match the patient's owner's region when there is a patient. */
    region: regionIdSchema,
    data: genericTaskDataSchema,
    assigneeRole: staffRoleSchema.nullable().optional(),
    assigneeClinicId: clinicIdSchema.nullable().optional(),
    assigneeStaffId: z.uuid().nullable().optional(),
    fileKeys: z
      .array(fileKeySchema)
      .max(
        MAX_FILE_ATTACHMENTS,
        `Too many files attached — maximum ${MAX_FILE_ATTACHMENTS} allowed`
      )
      .optional(),
  })
  .refine((d) => d.data.type === 'follow_up' || !!d.patientId, {
    message: 'Patient is required',
    path: ['patientId'],
  })
  .refine(
    (d) => {
      const hasRole = !!d.assigneeRole
      const hasStaff = !!d.assigneeStaffId
      return (hasRole && !hasStaff) || (!hasRole && hasStaff)
    },
    { message: 'Must assign to either a role or a specific staff member' }
  )
  .refine(
    (d) => {
      if (d.assigneeClinicId && !d.assigneeRole) return false
      return true
    },
    { message: 'Clinic assignment requires a role' }
  )
export type PostGenericTaskBody = z.infer<typeof postGenericTaskBodySchema>

export const postProcedureRequestTaskBodySchema = z.strictObject({
  patientId: z.uuid(),
  /** Must match the patient's owner's region. */
  region: regionIdSchema,
  data: z.strictObject({
    type: z.literal('procedure_request'),
    urgent: z.boolean().default(false),
    procedure: procedureTypeSchema,
    note: z.string().trim().min(1).max(1000),
  }),
})
export type PostProcedureRequestTaskBody = z.infer<typeof postProcedureRequestTaskBodySchema>

export const postTaskBodySchema = z.union([
  postProcedureRequestTaskBodySchema,
  postGenericTaskBodySchema,
])

export type PostTaskBody = z.infer<typeof postTaskBodySchema>

// ─── PATCH /tasks/:taskId ───────────────────────────────────────────

export const patchTaskBodySchema = z
  .object({
    // Content edits: title and type-specific data (e.g. due date, urgent flag, lab type).
    title: z.string().min(1).optional(),
    data: taskDataSchema.optional(),
    status: z.enum(['in_progress', 'completed', 'cancelled']).optional(),
    /** An instant to snooze until, or `null` to wake the task now. */
    snoozedUntil: z.iso.datetime().nullable().optional(),
    assigneeRole: staffRoleSchema.nullable().optional(),
    assigneeClinicId: clinicIdSchema.nullable().optional(),
    assigneeStaffId: z.uuid().nullable().optional(),
    comment: z.string().min(1).optional(),
  })
  .refine((d) => !d.snoozedUntil || new Date(d.snoozedUntil) > new Date(), {
    message: 'Snooze must end in the future',
    path: ['snoozedUntil'],
  })
  .refine(
    (d) => {
      // Only validate when assignment fields are being changed
      const touchesRole = d.assigneeRole !== undefined
      const touchesStaff = d.assigneeStaffId !== undefined
      if (!touchesRole && !touchesStaff) return true
      const hasRole = !!d.assigneeRole
      const hasStaff = !!d.assigneeStaffId
      return (hasRole && !hasStaff) || (!hasRole && hasStaff)
    },
    { message: 'Must assign to either a role or a specific staff member' }
  )
  .refine(
    (d) => {
      if (d.assigneeClinicId !== undefined && d.assigneeClinicId !== null && !d.assigneeRole) {
        return false
      }
      return true
    },
    { message: 'Clinic assignment requires a role' }
  )

export type PatchTaskBody = z.infer<typeof patchTaskBodySchema>

// ─── GET /tasks ─────────────────────────────────────────────────────

/** Filters, sorting, and pagination common to all task list endpoints. */
const baseTasksQuerySchema = z.object({
  /** Omit to list tasks across all regions. */
  region: regionIdSchema.optional(),
  status: z.array(taskListStatusSchema).min(1).optional(),
  type: taskTypeSchema.optional(),
  q: z.string().trim().optional(),
  /** Only return tasks created on or after this date (ISO 8601 date string). */
  createdAfter: z.iso.date().optional(),
  /**
   * Primary sort dimension. `urgency` (default) ranks urgent tasks first;
   * `due_date` ranks by soonest due date; `created_at` ranks by most recently
   * created first (useful for tasks without a due date).
   */
  sortBy: z.enum(['urgency', 'due_date', 'created_at']).optional(),
  /**
   * Sort direction. When omitted, each `sortBy` dimension falls back to its
   * natural default (urgency/created_at → `desc`, due_date → `asc`). The task
   * table toggles this by re-clicking the active column header.
   */
  sortOrder: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number<number>().int().min(1).max(200).optional(),
  offset: z.coerce.number<number>().int().min(0).optional(),
})

export const getTasksQuerySchema = baseTasksQuerySchema.extend({
  patientId: z.uuid().optional(),
  assigneeStaffId: z.uuid().optional(),
  assigneeRole: staffRoleSchema.optional(),
  assigneeClinicId: clinicIdSchema.optional(),
})

export type GetTasksQuery = z.infer<typeof getTasksQuerySchema>

/**
 * Query params for `GET /tasks/mine` — the current user's task scope (tasks
 * assigned to them personally, unioned with their team's tasks). Assignee /
 * role are implied by the authenticated caller, so the only assignment-related
 * param is an optional `clinicId` that narrows the team side for clinic-bound
 * roles.
 */
export const getMyTasksQuerySchema = baseTasksQuerySchema.extend({
  /** Narrows the team side of the scope to a clinic. Ignored for non-clinic roles. */
  clinicId: clinicIdSchema.optional(),
})

export type GetMyTasksQuery = z.infer<typeof getMyTasksQuerySchema>

export const taskIdParams = z.object({ taskId: z.uuid() })

// ─── POST /tasks/:taskId/comments ──────────────────────────────────

export const postTaskCommentBodySchema = z.object({ body: z.string().min(1) })
export type PostTaskCommentBody = z.infer<typeof postTaskCommentBodySchema>

// ─── Response types ─────────────────────────────────────────────────

export const taskResponseSchema = z.object({
  id: z.string(),
  type: taskTypeSchema,
  title: z.string(),
  status: taskStatusSchema,
  region: regionIdSchema,
  assigneeRole: staffRoleSchema.nullable(),
  patient: z.object({ id: z.string(), name: z.string() }).nullable(),
  creator: z.object({ id: z.string(), firstName: z.string(), lastName: z.string() }).nullable(),
  assignee: z.object({ id: z.string(), firstName: z.string(), lastName: z.string() }).nullable(),
  assigneeClinic: z.object({ id: z.string(), name: z.string() }).nullable(),
  data: taskDataSchema,
  files: z.array(z.object({ id: z.string(), fileName: z.string() })),
  comments: z.array(taskCommentSchema),
  snoozedUntil: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type TaskResponse = z.infer<typeof taskResponseSchema>

export const getTasksResponseSchema = z.object({
  tasks: z.array(taskResponseSchema),
  totalCount: z.number(),
})

export const getTaskResponseSchema = z.object({ task: taskResponseSchema })

/** `task` is null for a dental request (stamps the patient, no task row). */
export const postTaskResponseSchema = z.object({ task: taskResponseSchema.nullable() })
